import classNames from "classnames";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import React from "react";
import styles from "./ColumnFilterWrapper.module.scss";

export interface ColumnFilterWrapperProps {
  title?: React.ReactNode;
  subtitle?: string;
  filterCount?: number;
  isFiltered?: boolean;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  onSortChange?: (direction?: "asc" | "desc") => void;
  onClearSort?: () => void;
  sortAscLabel?: string;
  sortDescLabel?: string;
  showSort?: boolean;
  hasSort?: boolean;
  hasFilter?: boolean;
  onReset?: () => void;
  onApply?: () => void;
  onClose?: () => void;
  confirm?: () => void;
  close?: () => void;
  children?: React.ReactNode;
  before?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  showFooter?: boolean;
}

export const ColumnFilterWrapper: React.FC<ColumnFilterWrapperProps> = ({
  title,
  subtitle,
  filterCount = 0,
  isFiltered,
  sortActive = false,
  sortDirection = "desc",
  onSortChange,
  onClearSort,
  sortAscLabel = "Ascending",
  sortDescLabel = "Descending",
  showSort = false,
  hasSort,
  hasFilter,
  onReset,
  onApply,
  onClose,
  confirm,
  close,
  children,
  before,
  className,
  style,
  showFooter = true,
}) => {
  const handleClose = () => {
    if (onClose) onClose();
    else if (close) close();
    else if (confirm) confirm();
  };

  const handleApply = () => {
    if (onApply) onApply();
    if (confirm) confirm();
    else handleClose();
  };

  const handleReset = () => {
    if (onReset) onReset();
    if (sortActive) {
      if (onClearSort) onClearSort();
      else if (onSortChange) onSortChange(undefined);
    }
  };

  const isSortAvailable =
    hasSort !== undefined
      ? hasSort
      : showSort || !!onSortChange;

  const isFilterAvailable =
    hasFilter !== undefined
      ? hasFilter
      : !!children;

  let defaultSubtitle = "Filter Column";
  if (isSortAvailable && isFilterAvailable) {
    defaultSubtitle = "Sort & Filter Column";
  } else if (isSortAvailable) {
    defaultSubtitle = "Sort Column";
  } else if (isFilterAvailable) {
    defaultSubtitle = "Filter Column";
  }

  const formattedSubtitle = subtitle || defaultSubtitle;

  return (
    <div
      className={classNames(styles.wrapper, className)}
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      {before}
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <div className={styles.headerTitle}>{title || "Column"}</div>
          <div className={styles.headerSubtitle}>{formattedSubtitle}</div>
        </div>
        {/* <button
          type="button"
          onClick={handleClose}
          className={styles.doneBtn}
        >
          Done
        </button> */}
      </div>

      {/* Sort Section if enabled */}
      {(showSort || !!onSortChange) && (
        <div className={styles.sortSection}>
          <div className={styles.sortHeader}>
            <p className={styles.sortTitle}>Sort</p>
            {sortActive && (onClearSort || onSortChange) && (
              <button
                type="button"
                onClick={() => {
                  if (onClearSort) onClearSort();
                  else if (onSortChange) onSortChange(undefined);
                }}
                className={styles.clearSortBtn}
              >
                Clear
              </button>
            )}
          </div>
          <div className={styles.sortGrid}>
            <button
              type="button"
              onClick={() =>
                onSortChange?.(sortActive && sortDirection === "asc" ? undefined : "asc")
              }
              className={classNames(styles.sortBtn, {
                [styles.activeSort]: sortActive && sortDirection === "asc",
              })}
            >
              <ArrowUp size={13} />
              {sortAscLabel}
            </button>
            <button
              type="button"
              onClick={() =>
                onSortChange?.(sortActive && sortDirection === "desc" ? undefined : "desc")
              }
              className={classNames(styles.sortBtn, {
                [styles.activeSort]: sortActive && sortDirection === "desc",
              })}
            >
              <ArrowDown size={13} />
              {sortDescLabel}
            </button>
          </div>
        </div>
      )}

      {/* Filter Body */}
      {children && <div className={styles.bodyContent}>{children}</div>}

      {/* Footer */}
      {showFooter && (
        <div className={styles.footer}>
          <button
            type="button"
            onClick={handleReset}
            className={styles.resetBtn}
          >
            <X size={13} /> Reset column
          </button>
          <button
            type="button"
            onClick={handleApply}
            className={styles.applyBtn}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default ColumnFilterWrapper;
